import React, { useEffect, useMemo, useRef, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarController, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Line, Bar, Chart } from "react-chartjs-2";
import zoomPlugin from "chartjs-plugin-zoom";
import { getDateRange, getDayMonthYear } from "../utils/date";
import { isMobileDevice, getTitleFromThreartDrillKey, getThreatDrillKey, decodeThreatDrillKey, getColorForThreat } from "../utils/utils";
import "reactjs-popup/dist/index.css";
import "leaflet/dist/leaflet.css";
import "chart.js/auto";

// ChartJS.register(ChartDataLabels)

ChartJS.register(BarController, BarElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);
ChartJS.defaults.color = "white";
const zoomOptions = {
  pan: {
    enabled: !isMobileDevice(),
    mode: "x",
  },
  zoom: {
    wheel: {
      enabled: false,
    },
    pinch: {
      enabled: false,
    },
    mode: "x",
  },
};

const options = {
  responsive: true,
  plugins: {
    // legend: {
    //   position: "top",
    // },
    title: {
      display: true,
    },
    zoom: zoomOptions,
  },
};

const Charts = ({ data, reRenderCause, chartRef, chartWatchRef }) => {
  const getDataForCountForMinutesHours = (data_) => {
    const counterMinutes = {};
    const counterHours = {};
    const counterCities = {};
    const counterPerCity = {};
    const counterPerArea = {};

    for (let hour = 0; hour < 59; hour++) {
      counterMinutes[hour] = 0;
    }
    for (let hourS = 0; hourS < 24; hourS++) {
      const hour = ("0" + hourS).slice(-2);
      counterHours[hour] = 0;
    }

    data_.forEach(({ date, cities }) => {
      const min = date.getMinutes();
      if (counterMinutes[min]) counterMinutes[min]++;
      else counterMinutes[min] = 1;

      const hour = ("0" + date.getHours()).slice(-2);
      if (counterHours[hour]) counterHours[hour]++;
      else counterHours[hour] = 1;

      const string = getDayMonthYear(date);
      if (counterCities[string]) counterCities[string] = counterCities[string] + cities.length;
      else counterCities[string] = cities.length;

      const areasTotal = new Set();
      cities.forEach(({ name, areaName }) => {
        if (counterPerCity[name]) counterPerCity[name]++;
        else counterPerCity[name] = 1;
        areasTotal.add(areaName);
      });

      Array.from(areasTotal).forEach((areaName) => {
        if (counterPerArea[areaName]) counterPerArea[areaName]++;
        else counterPerArea[areaName] = 1;
      });
    });

    const titleMinutes = [];
    const bodyMinutes = [];

    Object.entries(counterMinutes)
      .sort((b, a) => (+a[0] < +b[0] ? -1 : 1))
      .map(([k, v]) => {
        titleMinutes.push(k);
        bodyMinutes.push(v);
      });

    const titleHours = [];
    const bodyHours = [];

    Object.entries(counterHours)
      .sort((b, a) => (+a[0] < +b[0] ? -1 : 1))
      .map(([k, v]) => {
        titleHours.push(k);
        bodyHours.push(v);
      });

    const titlePerCity = [];
    const bodyPerCity = [];

    Object.entries(counterPerCity)
      .sort((b, a) => (a[1] < b[1] ? -1 : 1))
      .map(([k, v]) => {
        titlePerCity.push(k);
        bodyPerCity.push(v);
      });

    const titlePerArea = [];
    const bodyPerArea = [];

    Object.entries(counterPerArea)
      .sort((b, a) => (a[1] < b[1] ? -1 : 1))
      .map(([k, v]) => {
        titlePerArea.push(k);
        bodyPerArea.push(v);
      });

    const titleCountbyCities = [];
    const bodyCountByCities = [];

    Object.entries(counterCities)
      .sort((b, a) => (+a[0] < +b[0] ? -1 : 1))
      .map(([k, v]) => {
        titleCountbyCities.push(k);
        bodyCountByCities.push(v);
      });

    return {
      minutes: {
        labels: titleMinutes.reverse(),
        datasets: [
          {
            data: bodyMinutes.reverse(),
            label: "כמות התרעות",
            borderColor: "#A52A2A",
            backgroundColor: "#A52A2A",
            borderRadius: 8,
            borderWidth: 1,
          },
        ],
      },
      hours: {
        labels: titleHours.reverse(),
        datasets: [
          {
            data: bodyHours.reverse(),

            label: "כמות התרעות",
            borderColor: "#A52A2A",
            backgroundColor: "#A52A2A",
            borderRadius: 8,
            borderWidth: 1,
          },
        ],
      },
      countByCities: {
        labels: titleCountbyCities,
        datasets: [
          {
            data: bodyCountByCities,

            label: "כמות התרעות",
            borderColor: "#A52A2A",
            backgroundColor: "#A52A2A",
            borderRadius: 8,
            borderWidth: 1,
          },
        ],
      },
      counterPerCity: {
        labels: titlePerCity,
        datasets: [
          {
            data: bodyPerCity,
            label: "כמות התרעות",
            borderColor: "#A52A2A",
            backgroundColor: "#A52A2A",
            borderRadius: 8,
            borderWidth: 1,
          },
        ],
      },
      counterPerArea: {
        labels: titlePerArea,
        datasets: [
          {
            data: bodyPerArea,
            label: "כמות התרעות",
            borderColor: "#A52A2A",
            backgroundColor: "#A52A2A",
            borderRadius: 8,
            borderWidth: 1,
          },
        ],
      },
    };
  };

  const getDataForCountTypes = (data_) => {
    const counter = {};

    const dates = [];
    const threats = [];

    const threatsMap = [...new Set(data_.map((t) => getThreatDrillKey(t.threat, t.isDrill)))];
    if (!data_?.[0]?.date)
      return {
        labels: [],
        datasets: [],
      };
    const dateRanges = getDateRange(data_[0].date, data_[data_.length - 1].date);

    dateRanges.forEach((date) => {
      const string = getDayMonthYear(date);
      threatsMap.forEach((t) => {
        counter[t] = {};
        counter[t][string] = 0;
      });
    });

    data_.forEach(({ date, threat, isDrill }) => {
      const threatStr = getThreatDrillKey(threat, isDrill);
      const string = getDayMonthYear(date);
      if (!dates.includes(string)) dates.push(string);
      if (!threats.includes(threatStr)) threats.push(threatStr);
    });

    threats.forEach((t) => {
      counter[t] = {};
      dates.forEach((d) => [(counter[t][d] = 0)]);
    });

    data_.forEach(({ date, threat, isDrill }) => {
      const threatStr = getThreatDrillKey(threat, isDrill);
      const string = getDayMonthYear(date);
      counter[threatStr][string]++;
    });

    const datasets = [];

    Object.entries(counter).map(([threat, values]) => {
      const title = [];
      const body = [];

      Object.entries(([date, value]) => {
        title.push(date);
        body.push(value);
      });

      const [threatVal, isDrill] = decodeThreatDrillKey(threat);
      datasets.push({
        data: Object.values(values),

        label: getTitleFromThreartDrillKey(threatVal, isDrill),
        borderColor: getColorForThreat(threatVal),
        backgroundColor: getColorForThreat(threatVal),
        borderRadius: 8,
        borderWidth: 1,
      });
    });

    return {
      labels: dates,
      datasets: datasets,
    };
  };

  const chartsData = useMemo(() => getDataForCountForMinutesHours(data), [data]);
  const typesChartData = useMemo(() => getDataForCountTypes(data), [data]);

  const citiesChartRef = React.useRef(null);
  const typesChartRef = React.useRef(null);
  const areasChartRef = React.useRef(null);
  const byCitiesChartRef = React.useRef(null);

  const handleResetZoom = () => {
    if (citiesChartRef && citiesChartRef.current?.scales?.x) {
      const chart = citiesChartRef.current;
      citiesChartRef.current.resetZoom();

      setTimeout(() => {
        if (chart.data.datasets[0].data.length > 40) citiesChartRef.current.zoom(Math.min(chart.data.datasets[0].data.length * 0.001999999 * 2 + 1, 1.97));

        setTimeout(() => {
          if (chart.data.datasets[0].data.length) chart.pan({ x: Infinity }, undefined, "default");
        }, 50);
      }, 50);
    }

    typesChartRef.current?.resetZoom();
    areasChartRef.current?.resetZoom();
    byCitiesChartRef.current?.resetZoom();
  };

  useEffect(() => {
    setTimeout(() => {
      handleResetZoom();
    }, 50);
  }, [data, reRenderCause]);

  return (
    <div
      className="charts flex flex-wrap "
      ref={(r) => {
        if (isMobileDevice()) {
          chartWatchRef.current = r;
          chartRef.current = r;
        } else {
          chartRef.current = r;
        }
      }}
    >
      <div className="sm:w-[50%] w-[100%]">
        <div className="flex justify-between">
          <h3 className="text-white font-bold  text-lg">שכיחות יישובים</h3>
          <div className="flex gap-5 ml-5 text-white">
            <button onClick={() => citiesChartRef.current.zoom(1.1)}>+</button>
            <button onClick={() => citiesChartRef.current.zoom(-1.1)}>-</button>
            <button onClick={() => citiesChartRef.current.pan({ x: 50 })}>{"<"}</button>
            <button onClick={() => citiesChartRef.current.pan({ x: -50 })}>{">"}</button>
            <button onClick={() => handleResetZoom()}>R</button>
          </div>
        </div>
        <Bar
          data={chartsData.counterPerCity}
          options={{
            ...options,
            maintainAspectRatio: true,
            plugins: {
              datalabels: {
                anchor: "end",
                align: "top",
                offset: 2,
                padding: {
                  left: 2,
                },
              },
              zoom: zoomOptions,
            },

            scales: {
              x: {
                reverse: true,
              },
            },
          }}
          ref={citiesChartRef}
        />
      </div>

      <div className="sm:w-[50%] w-[100%]">
        <div className="flex justify-between">
          <h3 className="text-white font-bold text-lg">שכיחות אזורים</h3>
          <div className="flex gap-5 ml-5 text-white">
            <button onClick={() => areasChartRef.current.zoom(1.1)}>+</button>
            <button onClick={() => areasChartRef.current.zoom(-1.1)}>-</button>
            <button onClick={() => areasChartRef.current.pan({ x: 50 })}>{"<"}</button>
            <button onClick={() => areasChartRef.current.pan({ x: -50 })}>{">"}</button>
            <button onClick={() => areasChartRef.current.resetZoom()}>R</button>
          </div>
        </div>
        <Bar
          ref={areasChartRef}
          data={chartsData.counterPerArea}
          options={{
            ...options,
            scales: {
              x: {
                reverse: true,
              },
            },
          }}
        />
      </div>
      <div className="sm:w-[50%] w-[100%]">
        <h3 className="text-white font-bold text-lg">התרעות לפי שעה</h3>
        <Bar data={chartsData.hours} options={options} />
      </div>
      <div className="sm:w-[50%] w-[100%]">
        <h3 className="text-white font-bold  text-lg">התרעות לפי דקות</h3>
        <Bar data={chartsData.minutes} options={options} />
      </div>

      <div className="sm:w-[50%] w-[100%]">
        <div className="flex justify-between">
          <h3 className="text-white font-bold text-lg" ref={!isMobileDevice() ? chartWatchRef : null}>
            סוגי התרעות
          </h3>

          <div className="flex gap-5 ml-5 text-white">
            <button onClick={() => typesChartRef.current.zoom(1.1)}>+</button>
            <button onClick={() => typesChartRef.current.zoom(-1.1)}>-</button>
            <button onClick={() => typesChartRef.current.pan({ x: -50 })}>{"<"}</button>
            <button onClick={() => typesChartRef.current.pan({ x: 50 })}>{">"}</button>
            <button onClick={() => typesChartRef.current?.resetZoom()}>R</button>
          </div>
        </div>
        <Chart type="line" ref={typesChartRef} data={typesChartData} options={options}></Chart>
      </div>

      <div className="sm:w-[50%] w-[100%]">
        <div className="flex justify-between">
          <h3 className="text-white font-bold text-lg ">התרעות ביום לפי כמות ישובים</h3>

          <div className="flex gap-5 ml-5 text-white">
            <button onClick={() => byCitiesChartRef.current.zoom(1.1)}>+</button>
            <button onClick={() => byCitiesChartRef.current.zoom(-1.1)}>-</button>
            <button onClick={() => byCitiesChartRef.current.pan({ x: -50 })}>{"<"}</button>
            <button onClick={() => byCitiesChartRef.current.pan({ x: 50 })}>{">"}</button>
            <button onClick={() => byCitiesChartRef.current?.resetZoom()}>R</button>
          </div>
        </div>
        <Chart type="line" ref={byCitiesChartRef} data={chartsData.countByCities} options={options}></Chart>
      </div>
    </div>
  );
};

export default Charts;
